import { v4 as uuidv4 } from 'uuid';
import { axiosClient } from '../../../../utils/axiosClient';
import { makeApiRequest, type ApiResponse } from '../../../utils';
import type { Cart } from '../../../../components/Donee/components/Capsules/Envelopes';
import type { User } from '../../../models';
import { GiftEndpoints } from './giftEndpoints';
import type { AddCardRequest, SendDonationRequest } from './requests';
import type { AddCardGiftResponse, SendDonationResponse } from './response';

type Expiry = {
    month: number;
    year: number;
};

export class GiftService {
    private expiryStringParser(expiry?: string): Expiry | undefined {
        if (!expiry) {
            return;
        }
        const expiration = expiry.split('/');
        if (expiration.length !== 2) {
            return;
        }
        const month = parseInt(expiration[0], 10);
        const year = parseInt('20' + expiration[1], 10);
        if (!month || !year) {
            return;
        }
        return {
            month,
            year,
        };
    }

    private mapUserToWallet(
        user: User,
        captchaToken: string | undefined,
    ): AddCardRequest {
        return {
            captchaToken,
            verification: captchaToken,
            billingInfo: {
                country: user.address?.countryCode,
                zipCode: user.address?.zip,
                state: user.address?.state,
                address: user.address?.street,
                city: user.address?.city,
            },
            cardInfo: {
                accountNumber: user.card?.cardNumber,
                accountHolderName: user.name,
                cvv: user.card?.cardCVV,
                expiration: this.expiryStringParser(user.card?.cardExpiryDate),
            },
        };
    }

    private mapToPayload(
        cart: Cart,
        doneeId: string,
        forceDuplicate: boolean,
        captchaToken: string | undefined,
    ): SendDonationRequest {
        return {
            doneeId,
            forceDuplicate: forceDuplicate ? 1 : 0,
            giftUuid: uuidv4(),
            memo: cart.message,
            paymentInstrumentId: cart.wallet?.paymentInstrumentUuid || '',
            recurrence: cart.recurring === 'None' ? null : cart.recurring,
            contributions: this.mapContributions(cart.envelopes),
            captchaToken,
            verification: captchaToken,
        };
    }

    private mapContributions(
        envelopes: Cart['envelopes'],
    ): SendDonationRequest['contributions'] {
        const result: SendDonationRequest['contributions'] = [];
        for (const key in envelopes) {
            if (key === 'new') {
                continue;
            }
            const envelope = envelopes[key];
            if (!envelope) {
                continue;
            }
            result.push({
                amount: envelope.amount,
                causeId: envelope.envelopeId,
            });
        }
        return result;
    }

    public async sendDonationRequest(
        cart: Cart,
        doneeId: string,
        forceDuplicate: boolean,
        captchaToken: string | undefined,
    ): Promise<ApiResponse<SendDonationResponse>> {
        const url = GiftEndpoints.donate();
        const httpRequest = axiosClient.post(
            url,
            this.mapToPayload(cart, doneeId, forceDuplicate, captchaToken),
        );
        const result = await makeApiRequest<SendDonationResponse>(httpRequest);
        return result;
    }

    public async addCardRequest(
        user: User,
        recaptcha: string | undefined,
    ): Promise<ApiResponse<AddCardGiftResponse>> {
        const url = GiftEndpoints.addCard();
        const httpRequest = axiosClient.post(
            url,
            this.mapUserToWallet(user, recaptcha),
        );
        const result = await makeApiRequest<AddCardGiftResponse>(httpRequest);
        return result;
    }
}
